import { Lock, ShieldCheck, HardDrive, EyeOff, UserRound, Activity, Moon, Sparkles, Ruler, CalendarClock, BadgeCheck, MapPin, ListTodo, LayoutDashboard } from "lucide-react";
import { Reveal, Chapter, SectionHeading } from "./Reveal";

const SECURITY = [
  [Lock, "Secure authentication", "Modern sign-in that keeps accounts protected."],
  [UserRound, "Private accounts", "Your profile and activity stay yours."],
  [ShieldCheck, "Controlled project access", "Only the people you invite get in."],
  [HardDrive, "Secure media storage", "Photos and documents stored safely."],
  [EyeOff, "Privacy controls", "Decide what others can see."],
  [Activity, "Activity controls", "Presence and read receipts on your terms."],
  [Moon, "Out of Hours", "User-controlled boundaries around availability."],
];

const FUTURE = [
  [ListTodo, "Recognising tasks"],
  [CalendarClock, "Identifying deadlines"],
  [Ruler, "Capturing measurements"],
  [MapPin, "Finding addresses"],
  [BadgeCheck, "Recording approvals"],
  [LayoutDashboard, "Building project summaries"],
];

export const Trust = () => (
  <section id="security" className="bg-diem-paper py-28 sm:py-36" data-testid="trust-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <Chapter index="12" label="Trust & what's next" />
      <div className="mt-8 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal delay={0.1}>
            <SectionHeading>Your conversations matter.</SectionHeading>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-7 text-lg leading-relaxed text-diem-slate max-w-lg">
              Deyem is being built with security and privacy at its core — with honest promises, not
              buzzwords.
            </p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-3">
            {SECURITY.map(([Icon, t, s], i) => (
              <Reveal key={t} delay={0.1 + i * 0.05} y={24}>
                <div className="rounded-2xl bg-white border border-diem-line p-5 h-full hover:border-diem-blue/30 transition-colors duration-300">
                  <Icon className="w-5 h-5 text-diem-blue" />
                  <p className="mt-3 font-semibold text-diem-ink text-[15px]">{t}</p>
                  <p className="mt-1 text-[13px] text-diem-muted leading-relaxed">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:pl-8">
          <Reveal delay={0.15}>
            <div className="rounded-3xl bg-diem-ink p-8 sm:p-10 text-white h-full flex flex-col" data-testid="future-intelligence-card">
              <span className="inline-flex items-center gap-2 self-start rounded-full bg-diem-blue/20 text-diem-sky text-[11px] font-bold px-3 py-1.5 uppercase tracking-wider">
                <Sparkles className="w-3.5 h-3.5" /> Where Deyem is heading
              </span>
              <h3 className="mt-6 font-display text-3xl sm:text-4xl font-medium tracking-tighter">Messaging gets smarter.</h3>
              <p className="mt-5 text-white/60 leading-relaxed">
                Deyem is being built to understand the useful information inside everyday conversations.
              </p>
              <div className="mt-8 space-y-2.5 flex-1">
                {FUTURE.map(([Icon, t], i) => (
                  <Reveal key={t} delay={0.2 + i * 0.06} y={16}>
                    <div className="flex items-center gap-3.5 rounded-xl bg-white/5 border border-white/10 px-4 py-3.5">
                      <Icon className="w-4.5 h-4.5 w-[18px] h-[18px] text-diem-sky shrink-0" />
                      <p className="text-sm font-semibold text-white/85">{t}</p>
                      <span className="ml-auto text-[10px] font-bold uppercase tracking-wider text-white/35">In development</span>
                    </div>
                  </Reveal>
                ))}
              </div>
              <p className="mt-8 text-[12px] text-white/35 leading-relaxed">
                We'll only ship intelligence when it genuinely works — and we'll never claim otherwise.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);
